import { useCallback, useEffect, useState } from 'react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Link } from '../lib/routing';
import { api } from '../lib/api';
import { Alert } from '@/components/ui/alert';
import { Eye, CaretRight } from '@phosphor-icons/react';

interface AssignmentOv {
  id: string;
  title: string;
  due_at: string | null;
  classrooms: string;
  submitted: number;
  total: number;
  average_score: number | null;
  letter_counts?: Record<string, number>;
}
interface ExamOv {
  id: string;
  exam_title: string;
  classroom_name: string;
  status: string;
  collected: number;
  total: number;
}

const EXAM_STATUS: Record<string, string> = {
  draft: 'Brouillon',
  collecting: 'Collecte',
  correcting: 'Correction',
  done: 'Terminé',
};

const logLoadError = (procedure: string, reason: unknown) => {
  const code = (reason as { data?: { code?: string } } | null)?.data?.code;
  console.error(`[supervision] ${procedure} a échoué`, { code, reason });
};

function BlockError({ subject, onRetry, loading, className }: { subject: string; onRetry: () => void; loading: boolean; className?: string }) {
  return (
    <Alert variant="destructive" className={className}>
      <p className="m-0">
        {subject} n'ont pas pu être chargés. Aucun chiffre n'est affiché tant que les données réelles ne sont pas
        disponibles.
      </p>
      <button type="button" onClick={onRetry} disabled={loading} className="mt-2 min-h-11 px-3 font-bold underline disabled:opacity-60">
        {loading ? 'Chargement…' : 'Réessayer'}
      </button>
    </Alert>
  );
}

const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : 0);

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 basis-[140px] flex flex-col gap-1.5 p-4 bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)]">
      <span className="text-[30px] font-black leading-none text-primary tabular-nums">{value}</span>
      <span className="text-[11px] font-bold tracking-[.06em] uppercase text-[var(--naia-text-secondary)]">{label}</span>
    </div>
  );
}

function Bar({ a, b }: { a: number; b: number }) {
  return (
    <div className="h-[6px] rounded-full bg-[var(--naia-line)] overflow-hidden mt-[6px] w-[120px] max-w-[40vw]">
      <div className="h-full bg-primary transition-[width]" style={{ width: `${pct(a, b)}%` }} />
    </div>
  );
}

export default function DirectionSupervision() {
  const [assignments, setAssignments] = useState<AssignmentOv[]>([]);
  const [exams, setExams] = useState<ExamOv[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);
  const [examsLoading, setExamsLoading] = useState(true);
  const [assignmentsFailed, setAssignmentsFailed] = useState(false);
  const [examsFailed, setExamsFailed] = useState(false);

  const loadAssignments = useCallback(async () => {
    setAssignmentsLoading(true);
    try {
      setAssignments((await api.direction.supervision.assignments.query()) as AssignmentOv[]);
      setAssignmentsFailed(false);
    } catch (reason) {
      setAssignmentsFailed(true);
      logLoadError('direction.supervision.assignments', reason);
    } finally {
      setAssignmentsLoading(false);
    }
  }, []);

  const loadExams = useCallback(async () => {
    setExamsLoading(true);
    try {
      setExams((await api.direction.supervision.exams.query()) as ExamOv[]);
      setExamsFailed(false);
    } catch (reason) {
      setExamsFailed(true);
      logLoadError('direction.supervision.exams', reason);
    } finally {
      setExamsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAssignments();
    void loadExams();
  }, [loadAssignments, loadExams]);

  const withStudents = assignments.filter((a) => a.total > 0);
  const avgRate = withStudents.length
    ? Math.round(withStudents.reduce((s, a) => s + pct(a.submitted, a.total), 0) / withStudents.length)
    : null;

  return (
    <>
      <HeaderContent
        width="md"
        showBurger
        eyebrow="Mode direction"
        eyebrowIcon={<Eye size={11} weight="bold" />}
      >
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Supervision</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Devoirs et examens de l'établissement — taux de rendu et statuts.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[880px] mx-auto px-5 pb-16 pt-4">

        <>
            <div className="flex flex-wrap gap-3 mb-8">
              {!assignmentsFailed && !assignmentsLoading && <KpiCard label="Devoirs" value={String(assignments.length)} />}
              {!assignmentsFailed && !assignmentsLoading && <KpiCard label="Taux de rendu moyen" value={avgRate === null ? '—' : `${avgRate}%`} />}
              {!examsFailed && !examsLoading && <KpiCard label="Examens" value={String(exams.length)} />}
            </div>

            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10.5px] font-extrabold tracking-[.12em] uppercase text-[var(--naia-text-secondary)]">Devoirs</span>
              {assignments.length > 0 && (
                <span className="text-[10px] font-black min-w-[18px] h-[18px] px-1.5 rounded-full flex items-center justify-center text-primary bg-primary/10">
                  {assignments.length}
                </span>
              )}
            </div>
            {assignmentsFailed ? (
              <BlockError subject="Les devoirs" onRetry={loadAssignments} loading={assignmentsLoading} className="mb-8" />
            ) : assignmentsLoading ? (
              <p className="text-[var(--naia-text-secondary)] text-[13.5px] mb-8">Chargement…</p>
            ) : assignments.length === 0 ? (
              <p className="text-[var(--naia-text-secondary)] text-[13.5px] mb-8">Aucun devoir dans l'établissement.</p>
            ) : (
              <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden divide-y divide-[var(--naia-line)] mb-8">
                {assignments.map((a) => (
                  <Link
                    key={a.id}
                    to={`/direction/supervision/devoirs/${a.id}`}
                    className="flex items-center gap-3 px-4 py-[13px] min-h-11 no-underline hover:bg-[var(--naia-info-bg)] transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[var(--naia-text-primary)]">{a.title}</div>
                      <div className="text-[13px] text-[var(--naia-text-secondary)] mt-0.5">{a.classrooms || '—'}</div>
                      <Bar a={a.submitted} b={a.total} />
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {Object.entries(a.letter_counts ?? {}).length > 0 && (
                        <span className="text-[12px] font-bold text-[var(--naia-text-secondary)] bg-[var(--naia-info-bg)] px-2.5 py-[5px] rounded-full">
                          {Object.entries(a.letter_counts ?? {})
                            .map(([letter, n]) => `${letter} ×${n}`)
                            .join(' · ')}
                        </span>
                      )}
                      {a.average_score !== null && (
                        <span className="text-[12px] font-bold text-[var(--naia-text-secondary)] bg-[var(--naia-info-bg)] px-2.5 py-[5px] rounded-full tabular-nums">
                          {Math.round(a.average_score * 100)}% moy.
                        </span>
                      )}
                      <span className="text-[12px] font-bold text-primary bg-primary/10 px-2.5 py-[5px] rounded-full tabular-nums">
                        {a.submitted}/{a.total} rendus
                      </span>
                      <CaretRight size={16} weight="bold" className="text-[var(--naia-text-secondary)]" />
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10.5px] font-extrabold tracking-[.12em] uppercase text-[var(--naia-text-secondary)]">Examens</span>
              {exams.length > 0 && (
                <span className="text-[10px] font-black min-w-[18px] h-[18px] px-1.5 rounded-full flex items-center justify-center text-primary bg-primary/10">
                  {exams.length}
                </span>
              )}
            </div>
            {examsFailed ? (
              <BlockError subject="Les examens" onRetry={loadExams} loading={examsLoading} />
            ) : examsLoading ? (
              <p className="text-[var(--naia-text-secondary)] text-[13.5px]">Chargement…</p>
            ) : exams.length === 0 ? (
              <p className="text-[var(--naia-text-secondary)] text-[13.5px]">Aucun examen dans l'établissement.</p>
            ) : (
              <div className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden divide-y divide-[var(--naia-line)]">
                {exams.map((e) => (
                  <Link
                    key={e.id}
                    to={`/direction/supervision/examens/${e.id}`}
                    className="flex items-center gap-3 px-4 py-[13px] min-h-11 no-underline hover:bg-[var(--naia-info-bg)] transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[var(--naia-text-primary)]">{e.exam_title}</div>
                      <div className="text-[13px] text-[var(--naia-text-secondary)] mt-0.5">{e.classroom_name} · {e.collected}/{e.total} copies</div>
                    </div>
                    <span className="text-[12px] font-bold text-primary bg-primary/10 px-2.5 py-[5px] rounded-full shrink-0">
                      {EXAM_STATUS[e.status] ?? e.status}
                    </span>
                    <CaretRight size={16} weight="bold" className="text-[var(--naia-text-secondary)] shrink-0" />
                  </Link>
                ))}
              </div>
            )}
        </>
      </main>
    </>
  );
}
